import { useEffect, useState } from 'react';

import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalOverlay, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/macro';
import { useUpdateEffect } from 'usehooks-ts';

import useFetchFfvLicense from '../../hooks/useFetchFfvLicense';
import useFormValidate from '../../hooks/useFormValidate';
import { FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME, SEX_FIELDNAME } from '../../hooks/useReadAdditionalData';
import { copyNonEmptyKeys } from '../../utils/arrayUtil';
import { getCorrectClientFieldName } from '../../utils/business/participants';
import { REQUEST_STATUS } from '../../utils/requestUtil';
import { COMMON_GAP } from '../../utils/styleUtil';
import AllSpecificData from '../business/checkoutdetails/specificdata/AllSpecificData';
import ButtonSubmit from '../generic/button/ButtonSubmit';
import { BIRTHDATE_FIELDNAME } from '../generic/form/InputBirthDate';
import PartnerFfvLicenses from './PartnerFfvClientLicenses';

const fieldsRequired = [FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME, getCorrectClientFieldName(BIRTHDATE_FIELDNAME), SEX_FIELDNAME];

const additionalDataNeeded = {
  standardData: fieldsRequired.map(label => ({ label, required: 1 })),
  customData: [],
};

const PartnerFfvPopup = ({ isOpen, onClose, relatedSessionGroupId, participantDetails }) => {
  const [ffvFormData, setFfvFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const { data, status, triggerFetchFfvLicense } = useFetchFfvLicense(relatedSessionGroupId);
  const hasSuccess = data && data.success && status === REQUEST_STATUS.OK;

  const { isInvalidForm } = useFormValidate();

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation(); // important to not propagate on P3 form

    if (isDisabled) return;
    if (isInvalidForm(e.nativeEvent.target)) {
      return;
    }

    setIsLoading(true);
    setIsDisabled(true);
    triggerFetchFfvLicense(ffvFormData);
  };

  const onDataUpdate = (customDataId, label, newValue, additionalData) => {
    setFfvFormData(prevData => ({
      ...prevData,
      [label]: newValue,
    }));
  };

  useUpdateEffect(() => {
    if (isDisabled) {
      setIsDisabled(false);
    }
  }, [JSON.stringify(ffvFormData)]);

  useUpdateEffect(() => {
    setIsLoading(false);
  }, [JSON.stringify(data)]);

  useEffect(() => {
    if (isOpen) {
      const newFormData = {};
      if (participantDetails != null && participantDetails.client != null) {
        copyNonEmptyKeys(participantDetails.client, newFormData, fieldsRequired);
      }
      setFfvFormData(newFormData);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent minW={{ base: '100%', sm: '460px' }}>
        <form onSubmit={handleSubmit}>
          <ModalCloseButton />
          <ModalBody mt={2}>
            <Text textAlign="center">
              <Trans>Fill the fields to check your license status.</Trans>
            </Text>

            {ffvFormData != null && (
              <AllSpecificData additionalDataNeeded={additionalDataNeeded} participant={ffvFormData} onParticipantDataUpdate={onDataUpdate} />
            )}
          </ModalBody>

          <ModalFooter pt="0" justifyContent="center" flexDirection="column" gap={COMMON_GAP}>
            <ButtonSubmit w="50%" isLoading={isLoading} isDisabled={isDisabled}>
              <Trans>Check license</Trans>
            </ButtonSubmit>

            {((data && !data.success) || status === REQUEST_STATUS.ERROR) && (
              <Text color="red">
                {data.message}
                {!data.message && <Trans>An error occurred, please try again later or contact an administrator.</Trans>}
              </Text>
            )}

            {hasSuccess && <PartnerFfvLicenses licenses={data.client_licenses} />}
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default PartnerFfvPopup;
